import { differenceInDays } from "date-fns";
import { z } from "zod";
import { Decimal, type TDecimal } from "../../utils/decimal.ts";
import type { PlainDate } from "../../utils/plain-date.ts";
import { type TypeId, typeid } from "../../utils/typeid.ts";
import type { GrafitUserId } from "../user/user.ts";
import type { HoursByDay } from "../worker-profile/worker-profile.ts";
import type { AutomaticReductionTiersSnapshot } from "./automatic-reduction-snapshot.ts";
import type { projectForListDTOSchema } from "./project.validation.ts";
import { PROJECT_STATUSES } from "./project-status-helpers.ts";

export const PROJECT_ID_PREFIX = "project";

export const priceRevisionScopeEnum = z.enum(["new-price", "market-price"]);
export const priceRevisionConfigEnum = z.enum(["REVISE_ALL_PRICES", "REVISE_TOTAL_PRICE"]);
export const projectStatusEnum = z.enum(PROJECT_STATUSES);

export const organisationInternalStatusEnum = z.enum(["waiting", "to-open", "opened", "do-not-open"]);

export type PriceRevisionConfig = z.infer<typeof priceRevisionConfigEnum>;
export type ProjectStatus = z.infer<typeof projectStatusEnum>;
export type OrganisationInternalStatus = z.infer<typeof organisationInternalStatusEnum>;

export interface Project {
  id: TypeId<typeof PROJECT_ID_PREFIX>;
  name: string;
  status: ProjectStatus;
  tenderReference: string;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  lastUpdatedBy: string;

  ownerId: GrafitUserId;

  versionOfProjectId: Project["id"] | null;
  /**
   * @member versionIndice - Public indice, displayed as a letter (0 = A). On a live project row, the one the next Project Version will take. On a versioned project row, the one it was saved under.
   **/
  versionIndice: number;
  /**
   * @member versionInternalIndice - Counter within a public indice. On a live project row, the next free one under `versionIndice`, taken by the next Project Version. On a versioned project row, the one it was saved under.
   **/
  versionInternalIndice: number;
  versionCheckpointAt: Date | null;
  /**
   * @member versionName - When defined on a live project row, the name copied to each new Project Version. When defined on a versioned project row, self explanatory.
   **/
  versionName: string | null;

  expectedStartDate: Date | null;
  expectedDuration: TDecimal | null;
  expectedEndDate: Date | null;
  deliveryDate: Date | null;
  awardedDate: PlainDate | null;

  vatRate: TDecimal;
  hoursByDay: HoursByDay;

  // Subsequent related fields
  sourceContractFrame: Project["id"] | null;
  parentQuoteId: Project["id"] | null;
  priceRevisionScope: z.infer<typeof priceRevisionScopeEnum>[];
  priceRevisionRate: TDecimal | null;
  /**
   * User defined reduction rate
   * Overrides the automatic tiers if not null
   */
  priceReductionRate: TDecimal | null;
  priceReductionAmount: TDecimal | null;

  automaticReductionApplyByBand: boolean;
  automaticReductionTiersSnapshot: AutomaticReductionTiersSnapshot | null;

  // Client information
  clientContractReference: string;
  siteLocation: string;

  // AO Price revision settings
  priceRevisionConfig: PriceRevisionConfig;
  priceRevisionConfigRate: TDecimal | null;

  sourceDealPriceRevisionConfig: PriceRevisionConfig | null;
  sourceDealPriceRevisionConfigRate: TDecimal | null;

  deprecated__tenderAnsweringProvisionRate: TDecimal | null;
  deprecated__tenderAnsweringProvisionHours: TDecimal | null;

  // Organization settings
  type: ProjectType;
  templateType: TemplateType | null;

  // Denormalized selling price, recomputed and persisted server-side after any project edit
  // (see the recompute middleware in shared-api-router). Null = not yet computed.
  computedPrice: TDecimal | null;

  internalQuoteReference: string | null;
  organisationInternalStatus: OrganisationInternalStatus | null;
  internalPurchaseReference: string | null;
}

export type ProjectDetailsStoreState = Project;

export type ProjectForListDTO = z.infer<typeof projectForListDTOSchema>;

export type ProjectForList<TProject extends ProjectForListDTO = ProjectForListDTO> = TProject & {
  childProjects?: ProjectForList<TProject>[];
};

export type QuoteForListDTO = Omit<ProjectForListDTO, "type"> & {
  type: "QUOTE";
};

export type QuoteForList = QuoteForListDTO;

export const DEFAULT_PROJECT_TRAVEL_ALLOWANCE = null;

export const MAX_PROJECT_NAME_LENGTH = 256;
export const MAX_VERSION_NAME_LENGTH = 256;
export const MAX_CLIENT_NAME_LENGTH = 256;
export const MAX_TENDER_REFERENCE_LENGTH = 256;
export const MAX_INTERNAL_QUOTE_REFERENCE_LENGTH = 64;
export const MAX_INTERNAL_PURCHASE_REFERENCE_LENGTH = 64;

export function normalizeInternalReference(input: string | null | undefined): string | null {
  const trimmed = input?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Normalizes a partial project patch: runs `internalQuoteReference` and `internalPurchaseReference`,
 * when set, through `normalizeInternalReference`; leaves the rest unchanged.
 * Shared between the update-project use-case and the frontend optimistic update.
 */
export function normalizeProjectPatchInternalReferences<
  T extends Record<string, unknown> & {
    internalQuoteReference?: string | null;
    internalPurchaseReference?: string | null;
  },
>(patch: T): T {
  return {
    ...patch,
    ...("internalQuoteReference" in patch && {
      internalQuoteReference: normalizeInternalReference(patch.internalQuoteReference),
    }),
    ...("internalPurchaseReference" in patch && {
      internalPurchaseReference: normalizeInternalReference(patch.internalPurchaseReference),
    }),
  };
}

/** @internal (knip flags this as un necessary export since it's only imported in a test file) */
export function sanitizeProjectPriceReduction<
  T extends Record<string, unknown> & { priceReductionRate?: TDecimal | null; priceReductionAmount?: TDecimal | null },
>(patch: T): T {
  if (patch.priceReductionAmount !== null && patch.priceReductionAmount !== undefined) {
    return { ...patch, priceReductionRate: null };
  }
  if (patch.priceReductionRate !== null && patch.priceReductionRate !== undefined) {
    return { ...patch, priceReductionAmount: null };
  }
  return patch;
}

export const DEFAULT_VAT_RATE = Decimal(20);

export const projectTypeEnum = z.enum(["TENDER", "DEAL", "QUOTE"]);

export type ProjectType = z.infer<typeof projectTypeEnum>;

export const templateTypeEnum = z.enum(["projectTemplate", "bidrowTemplate"]);
export type TemplateType = z.infer<typeof templateTypeEnum>;

export const isAnyTemplate = (project: {
  templateType?: Project["templateType"] | undefined;
}): project is { templateType: NonNullable<Project["templateType"]> } =>
  project.templateType !== null && project.templateType !== undefined;

export const shouldAllocateInternalQuoteReference = (project: {
  type: ProjectType;
  templateType?: Project["templateType"];
}): boolean => !isAnyTemplate(project) && (project.type === "TENDER" || project.type === "QUOTE");

export const versionSourceEnum = z.enum(["EXPORT_QUOTE", "AUTO", "MANUAL_SAVE"]);
export type VersionSource = z.infer<typeof versionSourceEnum>;

/**
 * pg does not support erasing enum members after creation.
 * @member MANUAL - deprecated, and never written - superseded by EXPORT_QUOTE
 * @member RESTORE - never written - the state saved before a restore is an AUTO version
 */
export const storedVersionSourceEnum = z.enum(["MANUAL", "AUTO", "EXPORT_QUOTE", "MANUAL_SAVE", "RESTORE"]);
export type StoredVersionSource = z.infer<typeof storedVersionSourceEnum>;

export const toVersionSource = (storedVersionSource: StoredVersionSource): VersionSource => {
  if (storedVersionSource === "MANUAL") {
    return "EXPORT_QUOTE";
  }
  if (storedVersionSource === "RESTORE") {
    return "AUTO";
  }
  return storedVersionSource;
};

const PROJECT_VERSION_CHECKPOINT_INTERVAL_MS = 12 * 60 * 60 * 1000;

export function isProjectVersionCheckpointExpired({
  checkpoint,
  now,
}: {
  checkpoint: Project["versionCheckpointAt"];
  now: Date;
}): boolean {
  return checkpoint === null || now.getTime() - checkpoint.getTime() >= PROJECT_VERSION_CHECKPOINT_INTERVAL_MS;
}

export const generateProjectId = () => typeid(PROJECT_ID_PREFIX);

export type QuoteProject = Project & { type: typeof projectTypeEnum.enum.QUOTE; sourceContractFrame: Project["id"] };
type RootQuoteProject = QuoteProject & { parentQuoteId: null };
type AdditionalWorkQuoteProject = QuoteProject & { parentQuoteId: Project["id"] };

export type VersionedProject = Project & { versionOfProjectId: Project["id"] };

export const isQuoteProject = (project: Project): project is QuoteProject => {
  return project.type === projectTypeEnum.enum.QUOTE && project.sourceContractFrame !== null;
};

export function projectOnlyUsesForcedPrices(projectType: ProjectType): boolean {
  return projectType === projectTypeEnum.enum.DEAL || projectType === projectTypeEnum.enum.QUOTE;
}

export const isVersionedProject = <TProject extends Pick<Project, "versionOfProjectId">>(
  project: TProject,
): project is TProject & { versionOfProjectId: Project["id"] } => {
  return project.versionOfProjectId !== null;
};

export const canEditProject = (project: {
  versionOfProjectId: Project["versionOfProjectId"];
  deletedAt?: Date | null;
}): boolean => {
  return project.versionOfProjectId === null && !project.deletedAt;
};

export const isAdditionalWorkQuote = (project: QuoteProject): project is AdditionalWorkQuoteProject => {
  return project.parentQuoteId !== null;
};

export const isRootQuote = (project: QuoteProject): project is RootQuoteProject => {
  return project.parentQuoteId === null;
};

const QUOTE_OR_TENDER_TO_SEND_OBSOLETE_DAYS_AFTER_DELIVERY = 15;

export const isQuoteOrTenderToSend = (project: {
  type: ProjectType;
  status: ProjectStatus;
  deliveryDate: Date | null;
}): boolean => {
  if (!["QUOTE", "TENDER"].includes(project.type)) {
    return false;
  }
  if (project.status !== "IN_PROGRESS") {
    return false;
  }
  if (project.deliveryDate !== null) {
    const daysDifference = differenceInDays(Date.now(), project.deliveryDate);
    if (daysDifference >= QUOTE_OR_TENDER_TO_SEND_OBSOLETE_DAYS_AFTER_DELIVERY) {
      return false;
    }
  }
  return true;
};

export const isQuoteOrTenderWaitingForResponse = (project: { type: ProjectType; status: ProjectStatus }): boolean => {
  return (
    ["QUOTE", "TENDER"].includes(project.type) &&
    ["WAITING_FOR_FIRST_RESPONSE", "IN_NEGOTIATION"].includes(project.status)
  );
};

export const sortByDeliveryDate = <T extends { deliveryDate: Date | null }>(
  projects: T[],
  options?: { order: "ASC" | "DESC" },
): T[] => {
  const order = options?.order ?? "ASC";
  return projects.toSorted((a, b) => {
    if (a.deliveryDate === null && b.deliveryDate === null) {
      return 0;
    }
    if (a.deliveryDate === null) {
      return 1;
    }
    if (b.deliveryDate === null) {
      return -1;
    }
    const diff = a.deliveryDate.getTime() - b.deliveryDate.getTime();
    return order === "ASC" ? diff : -diff;
  });
};
